import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {UrlBuilderService} from '../../../services/url-builder.service';
import {WPPostNative} from '../../../types/WPPostNative';
import {NativePuffTypeEnum} from '../../../enums/AdEnums';
import {WPMediaFlat} from '../../../types/WPMediaFlat';
import {ToolsService} from '../../../services/tools.service';
import {WpService} from '../../../services/wp.service';
import {DLPageTypeEnum} from '../../../enums/DataLayerEnums';
import {DataLayerParamsClickGA4} from '../../../types/DataLayerParamsClickGA4';

@Component({
  selector: 'app-native-puff',
  templateUrl: './native-puff.component.html',
  styleUrls: ['./native-puff.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NativePuffComponent {
  puffDataMultitype = input<any>(null);
  puffData = input<WPPostNative>(null);
  index = input<number>(null);
  numberOfPriorityPuffs = input<number>(1);
  qaHookPreffix = input<string>(null);
  pageType = input<DLPageTypeEnum>(null);
  forceContentCssClass = input<string>(null);
  adNamePreffix = input<string>(null);
  puffType = input<NativePuffTypeEnum | string>(null);
  public puffTitle = signal<string>('');
  public puffContent = signal<string>('');
  public puffCaption = signal<string>('');
  public puffImage = signal<WPMediaFlat>(null);
  public puffDfpPixelName = signal<string>('');
  public isSponsored = signal<boolean>(null);
  public puffDataToUse = computed<WPPostNative>(() => {
    return (this.puffDataMultitype() as WPPostNative) ?? this.puffData();
  });
  public adName = computed<string>(() => {
    const preffix = this.adNamePreffix();
    const puff = this.puffDataToUse();
    return preffix && puff ? `${preffix} ${puff.acf.trackingTitle}` : null;
  });
  public hasImage = computed<boolean>(() => {
    const puff = this.puffDataToUse();
    return !!puff && (puff.featured_media > 0 || puff.acf.mainImage != null);
  });
  public contentCssClass = computed<string>(() => this.forceContentCssClass() ?? 'py-6 px-4 md:px-0');
  private tools = inject(ToolsService);
  private urlBuilder = inject(UrlBuilderService);
  public routePath = computed<string[]>(() => {
    const puff = this.puffDataToUse();
    if (!puff) return [];

    return puff.acf.redirectedToUrl
      ? this.urlBuilder.convertRelativeUrlToRouterLink(puff.acf.redirectedToUrl)[0]
      : this.urlBuilder.CreateNativePostRouterLink(puff.id, decodeURI(puff.slug));
  });
  private wpService = inject(WpService);
  public customGa4Params = computed<DataLayerParamsClickGA4>(() => {
    if (!this.puffDataToUse()) {
      return null;
    }
    return this.wpService.createDataLayerGa4ClickCustomParams(
      this.puffDataToUse(),
      `${this.adName()} - ad click`,
      this.pageType(),
      '',
    );
  });
  private cssPreffix = computed<string>(() => this.qaHookPreffix() ?? 'test-native-puff');

  public titleCssClass = computed<string>(
    () => `${this.cssPreffix()}-title${this.tools.isNil(this.index()) ? '' : `-${this.index()}`}`,
  );

  public btnCssClass = computed<string>(
    () => `${this.cssPreffix()}-morebtn${this.tools.isNil(this.index()) ? '' : `-${this.index()}`}`,
  );

  constructor() {
    effect(
      () => {
        if (this.puffType() && this.puffDataToUse()) {
          this.assignPuffData();
        }
      },
      {allowSignalWrites: true},
    );
  }

  private assignPuffData(): void {
    if (this.puffType() === NativePuffTypeEnum.HOME) {
      this.puffTitle.set(this.puffDataToUse().acf.homePuffTitle);
      this.puffContent.set(this.puffDataToUse().acf.homePuffContent);
      this.puffCaption.set(this.puffDataToUse().acf.homePuffCaption);
      this.puffImage.set(this.puffDataToUse().acf.homePuffImage);
      this.puffDfpPixelName.set(this.puffDataToUse().acf.homePuffDfpPixelName);
    }

    if (this.puffType() === NativePuffTypeEnum.SIDEBAR) {
      this.puffTitle.set(this.puffDataToUse().acf.sidebarPuffTitle);
      this.puffContent.set(this.puffDataToUse().acf.sidebarPuffContent);
      this.puffCaption.set(this.puffDataToUse().acf.sidebarPuffCaption);
      this.puffImage.set(this.puffDataToUse().acf.sidebarPuffImage);
      this.puffDfpPixelName.set(this.puffDataToUse().acf.sidebarPuffDfpPixelName);
    }
    this.isSponsored.set(this.puffDataToUse().acf.isSponsored);
  }
}
